import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MDText } from 'i18n-react';
import uniqBy from 'lodash/uniqBy';
import partition from 'lodash/partition';
import Text from '@adsk/bim360-matrix-react-text';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import OverflowTooltip from '@adsk/bim360-matrix-react-overflow-tooltip';
import './_AssigneeCell.scss';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import { ThemeConsumer, Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';

const UNASSIGNED = 'Unassigned';

export class AssigneeCell extends React.PureComponent {
  constructor(props) {
    super(props);

    this.getAssigneesFromProps(props);

    this.setLabelElement = this.setLabelElement.bind(this);
    this.popoverAdditionalDataContent = this.popoverAdditionalDataContent.bind(this);
    this.popoverOverflowContent = this.popoverOverflowContent.bind(this);
    this.getTooltipParams = this.getTooltipParams.bind(this);
    this.getOverflowTooltip = this.getOverflowTooltip.bind(this);
    this.getAdditionalDataTooltip = this.getAdditionalDataTooltip.bind(this);
    this.getAssigneeName = this.getAssigneeName.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.getAssigneesFromProps(nextProps);
  }

  getAssigneesFromProps(props) {
    if (props.secondaryAssignees.length > 0) {
      this.secondaryAssignees = uniqBy(props.secondaryAssignees, 'id');
      const primaryAssigneeIsAlsoSecondary =
        this.secondaryAssignees.findIndex(
          assignee => assignee && assignee.id === props.primaryAssignee.id
        ) !== -1;
      const partitionedSecondaryAssignees = partition(this.secondaryAssignees, assignee =>
        this.checkIfAssigneeRelatesToUser(assignee, props.currentUser)
      );
      this.relatedSecondaryAssignees = partitionedSecondaryAssignees[0].sort(
        this.props.sortAssigneesByRelevance
      );
      this.notRelatedSecondaryAssignees = partitionedSecondaryAssignees[1];
      this.hasSecondaryAssignees = true;
      this.numberOfUniqueAssignees =
        primaryAssigneeIsAlsoSecondary || props.primaryAssignee.id === UNASSIGNED
          ? this.secondaryAssignees.length
          : this.secondaryAssignees.length + 1;
    } else {
      this.secondaryAssignees = this.relatedSecondaryAssignees = this.notRelatedSecondaryAssignees = [];
      this.hasSecondaryAssignees = false;
      this.numberOfUniqueAssignees = 1;
    }

    this.mainAssignee = this.getMainAssignee(props);
  }

  checkIfAssigneeRelatesToUser(assignee, user) {
    const assigneeId = assignee.id;
    return user.id === assigneeId || user.company.id === assigneeId || !!user.rolesMap[assigneeId];
  }

  /**
   * Main assignee is either an assignee that the current user relates to or the primary assignee itself
   * In case several assignees relates to the current user, this is the priority:
   * 1. Primary Assignee
   * 2. Secondary Assignee - User
   * 3. Secondary Assignee - Company
   * 4. Secondary Assignee - Role
   */
  getMainAssignee(props) {
    if (this.checkIfAssigneeRelatesToUser(props.primaryAssignee, props.currentUser)) {
      return { assignee: props.primaryAssignee, relatesToCurrentUser: true };
    }

    if (this.relatedSecondaryAssignees.length) {
      return { assignee: this.relatedSecondaryAssignees[0], relatesToCurrentUser: true };
    }

    return { assignee: props.primaryAssignee, relatesToCurrentUser: false };
  }

  getAssigneeName(assignee) {
    const { [TSymbolProp]: T } = this.props;
    return (
      <ThemeConsumer>
        {({ theme }) =>
          assignee.name ||
          (assignee.id === UNASSIGNED
            ? T.translate('UNASSIGNED')
            : T.translate(theme === Themes.ALLOY ? 'FORMER_MEMBER' : 'FORMER_USER'))
        }
      </ThemeConsumer>
    );
  }

  getAssigneeNameWrapped(assignee) {
    const mainAssigneeClasses = classNames(
      'AssigneeCell__main-assignee',
      { 'AssigneeCell__main-assignee--highlight': this.mainAssignee.relatesToCurrentUser },
      { 'AssigneeCell__main-assignee--dark': !this.mainAssignee.assignee.name },
      this.props.className
    );

    return <span className={mainAssigneeClasses}>{this.getAssigneeName(assignee)}</span>;
  }

  popoverAdditionalDataContent() {
    const additionalSecondaryAssigneesNum =
      this.secondaryAssignees.length - this.props.secondaryAssigneesPopoverMax;

    const primaryAssigneeClasses = classNames({
      'AssigneeCell__popover-row--dark': !this.props.primaryAssignee.name
    });
    const primaryAssigneeHighlightClass = classNames({
      'AssigneeCell__popover-text-content--highlighted': this.checkIfAssigneeRelatesToUser(
        this.props.primaryAssignee,
        this.props.currentUser
      )
    });

    return (
      <div className="AssigneeCell__popover-content-container">
        <div className="AssigneeCell__popover-row AssigneeCell__popover-title">
          {this.props.primaryAssigneePopoverTitle}
        </div>
        <div className="AssigneeCell__popover-primary-assignee-row">
          <Text className={primaryAssigneeClasses} hideTooltip={true} ellipsis={true}>
            <span className={primaryAssigneeHighlightClass}>
              {this.getAssigneeName(this.props.primaryAssignee)}
            </span>
          </Text>
        </div>
        <div className="AssigneeCell__popover-row AssigneeCell__popover-title AssigneeCell__secondary-assignee-title">
          {this.props.secondaryAssigneesPopoverTitle}
        </div>
        {this.relatedSecondaryAssignees.map(assignee => (
          <div key={assignee.id} className="AssigneeCell__popover-row">
            <Text hideTooltip={true} ellipsis={true}>
              <span className="AssigneeCell__popover-text-content--highlighted">
                {this.getAssigneeName(assignee)}
              </span>
            </Text>
          </div>
        ))}
        {this.notRelatedSecondaryAssignees
          .slice(0, this.props.secondaryAssigneesPopoverMax - this.relatedSecondaryAssignees.length)
          .map(assignee => (
            <Text
              key={assignee.id}
              className="AssigneeCell__popover-row"
              hideTooltip={true}
              ellipsis={true}
            >
              {this.getAssigneeName(assignee)}
            </Text>
          ))}
        {additionalSecondaryAssigneesNum > 0 ? (
          <div className="AssigneeCell__popover-row AssigneeCell__popover-row--dark">
            {this.props[TSymbolProp].translate('MORE_VALUES', {
              numberOfElements: additionalSecondaryAssigneesNum
            })}
          </div>
        ) : null}
      </div>
    );
  }

  popoverOverflowContent() {
    const { primaryAssignee, companyName } = this.props;
    return (
      <React.Fragment>
        <div className="AssigneeCell__popover-content-container-single-assignee">
          {this.getAssigneeNameWrapped(primaryAssignee)}
        </div>
        {companyName && (
          <span className="AssigneeCell__popover-content-container-company">{companyName}</span>
        )}
      </React.Fragment>
    );
  }

  setLabelElement(el) {
    this.label = el;
  }

  getTooltipParams(hasAdditionalData) {
    return {
      delayShow: 100,
      delayHide: 100,
      triggerTarget: Tooltip.TriggerTargets.TOOLTIP,
      container: this.props.container,
      tooltipClassName: classNames('AssigneeCell__popover-container', {
        'AssigneeCell__popover-container--with-secondary-assignee': hasAdditionalData
      }),
      className: 'AssigneeCell__popover'
    };
  }

  getOverflowTooltip() {
    const { companyName } = this.props;
    return (
      <OverflowTooltip
        {...this.getTooltipParams()}
        content={this.popoverOverflowContent()}
        autoTipOnEllipsis={true}
      >
        <span className="AssigneeCell__main-assignee-text-container" ref={this.setLabelElement}>
          {this.getAssigneeNameWrapped(this.mainAssignee.assignee)}
        </span>
        {companyName && <span className="AssigneeCell__main-assignee-company">{companyName}</span>}
      </OverflowTooltip>
    );
  }

  getAdditionalDataTooltip() {
    return (
      <Tooltip {...this.getTooltipParams(true)} content={this.popoverAdditionalDataContent()}>
        <div className="AssigneeCell__cell-with-multiple-assignees">
          <span className="AssigneeCell__main-assignee-text-container" ref={this.setLabelElement}>
            {this.getAssigneeNameWrapped(this.mainAssignee.assignee)}
          </span>
          <span className="AssigneeCell__plus-assignees">{`+${
            this.mainAssignee.assignee.id === UNASSIGNED
              ? this.numberOfUniqueAssignees
              : this.numberOfUniqueAssignees - 1
          }`}</span>
        </div>
      </Tooltip>
    );
  }

  render() {
    if (this.mainAssignee.assignee.id === UNASSIGNED && !this.hasSecondaryAssignees) return null;

    const hasShowMore =
      this.props.primaryAssignee.id === UNASSIGNED || this.numberOfUniqueAssignees > 1;
    return hasShowMore ? this.getAdditionalDataTooltip() : this.getOverflowTooltip();
  }
}

AssigneeCell.defaultProps = {
  secondaryAssignees: [],
  primaryAssignee: {
    id: UNASSIGNED
  },
  secondaryAssigneesPopoverMax: 4
};
const user = {
  id: PropTypes.string,
  company: PropTypes.shape({ id: PropTypes.string.isRequired }),
  rolesMap: PropTypes.objectOf(PropTypes.any)
};

const assignee = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string
};

AssigneeCell.propTypes = {
  /**
   * The First level of relation to the assignee.
   */
  primaryAssignee: PropTypes.shape(assignee),
  /**
   * company name of primary assignee
   */
  companyName: PropTypes.string,
  /**
   * Secondary level of relation. For example section assignee in Checklist.
   */
  secondaryAssignees: PropTypes.arrayOf(PropTypes.shape(assignee)),
  /**
   * The current user which uses the application.
   */
  currentUser: PropTypes.shape(user).isRequired,
  primaryAssigneePopoverTitle: PropTypes.string,
  secondaryAssigneesPopoverTitle: PropTypes.string,
  /**
   * Num of assignees will be shown in the tooltip
   */
  secondaryAssigneesPopoverMax: PropTypes.number,
  /**
   * Should sort the secondary assignees by relevance. For example, show assignees by user before company and role.
   */
  sortAssigneesByRelevance: PropTypes.func,
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  className: PropTypes.string,
  locale: PropTypes.string
};

export default withLocale(
  AssigneeCell,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
